// FILE: pullRequestsReactQuery.ts
// Purpose: react-query wiring for the Pull Requests section — list, detail, activity and the
//          write actions a reviewer takes from it, all over the WS transport (see
//          `pullRequestsList` etc. in `wsNativeApi.ts`).
// Layer: Client data-fetching support
// Exports: query/mutation option builders, query and mutation keys, cache invalidation helpers

import type {
  PullRequestActivityInput,
  PullRequestErrorReason,
  PullRequestCommentInput,
  PullRequestDetailInput,
  PullRequestInvalidateInput,
  PullRequestListInput,
  PullRequestRef,
  PullRequestRunActionInput,
} from "@taskin-code/contracts";
import { mutationOptions, queryOptions, type QueryClient } from "@tanstack/react-query";
import { ensureNativeApi } from "../nativeApi";

// A search page across every project the reader may see — cheap to re-ask, but not so cheap it
// should refetch on every focus flicker. Mirrors `GIT_STATUS_STALE_TIME_MS`.
const PULL_REQUEST_LIST_STALE_TIME_MS = 30_000;
// Twice the server's own list TTL, deliberately. A tick shorter than that cache would reach
// GitHub every single time, and the cost is multiplied by every project the reader can see and
// by every reader who left the tab open — against a budget of 5000 requests an hour per token.
// Nobody asked for that refresh; the ones somebody does ask for go through `invalidate`.
const PULL_REQUEST_LIST_REFETCH_INTERVAL_MS = 120_000;
// One pull request's detail and activity change less often than the list (a reviewer opens one
// row and reads it for a while), and neither polls on its own — a write action invalidates them
// directly instead. The stale times match the server's TTLs for the same reads, so a refetch
// inside that window is answered from its cache rather than from GitHub.
const PULL_REQUEST_DETAIL_STALE_TIME_MS = 15_000;
const PULL_REQUEST_ACTIVITY_STALE_TIME_MS = 15_000;
// The patch is pinned to a commit rather than to a clock — the server caches it against the head
// SHA — so re-reading it inside a session is nearly always re-reading the same bytes. It is also
// the single most expensive thing this section fetches, which is why the code tab does not ask
// for it until somebody opens it.
const PULL_REQUEST_DIFF_STALE_TIME_MS = 5 * 60_000;

export const pullRequestQueryKeys = {
  all: ["pull-requests"] as const,
  list: (input: PullRequestListInput) => ["pull-requests", "list", input] as const,
  detail: (ref: PullRequestRef) => ["pull-requests", "detail", ref.projectId, ref.number] as const,
  activity: (ref: PullRequestRef) =>
    ["pull-requests", "activity", ref.projectId, ref.number] as const,
  diff: (ref: PullRequestRef) => ["pull-requests", "diff", ref.projectId, ref.number] as const,
};

export const pullRequestMutationKeys = {
  runAction: ["pull-requests", "mutation", "run-action"] as const,
  comment: ["pull-requests", "mutation", "comment"] as const,
  invalidate: ["pull-requests", "mutation", "invalidate"] as const,
};

/** Forgets every list page, across every project and filter combination. */
export function invalidatePullRequestListQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ["pull-requests", "list"] as const });
}

/**
 * Forgets one pull request's detail, activity and patch — what a write action on that PR touches.
 * The patch is included because merging or pushing moves the head commit the server pinned it to.
 */
export function invalidatePullRequestDetailQueries(queryClient: QueryClient, ref: PullRequestRef) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: pullRequestQueryKeys.detail(ref) }),
    queryClient.invalidateQueries({ queryKey: pullRequestQueryKeys.activity(ref) }),
    queryClient.invalidateQueries({ queryKey: pullRequestQueryKeys.diff(ref) }),
  ]);
}

/** Forgets everything this section has cached — list, every detail and every activity. */
export function invalidatePullRequestQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: pullRequestQueryKeys.all });
}

export function pullRequestsListQueryOptions(input: PullRequestListInput) {
  return queryOptions({
    queryKey: pullRequestQueryKeys.list(input),
    queryFn: async () => {
      const api = ensureNativeApi();
      return api.pullRequests.list(input);
    },
    staleTime: PULL_REQUEST_LIST_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: PULL_REQUEST_LIST_REFETCH_INTERVAL_MS,
    // Explicit: a hidden tab must not spend the token's budget. Focus and reconnect already
    // bring a backgrounded reader up to date the moment they come back.
    refetchIntervalInBackground: false,
  });
}

export function pullRequestsDetailQueryOptions(ref: PullRequestDetailInput | null) {
  return queryOptions({
    queryKey: ref ? pullRequestQueryKeys.detail(ref) : (["pull-requests", "detail", null] as const),
    queryFn: async () => {
      const api = ensureNativeApi();
      if (!ref) throw new Error("Pull request detail is unavailable.");
      return unwrapAnswer(await api.pullRequests.detail(ref)).detail;
    },
    enabled: ref !== null,
    staleTime: PULL_REQUEST_DETAIL_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function pullRequestsActivityQueryOptions(ref: PullRequestActivityInput | null) {
  return queryOptions({
    queryKey: ref
      ? pullRequestQueryKeys.activity(ref)
      : (["pull-requests", "activity", null] as const),
    queryFn: async () => {
      const api = ensureNativeApi();
      if (!ref) throw new Error("Pull request activity is unavailable.");
      return unwrapAnswer(await api.pullRequests.activity(ref)).activity;
    },
    enabled: ref !== null,
    staleTime: PULL_REQUEST_ACTIVITY_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * A pull request's unified patch.
 *
 * The one read in this section that does not go over the websocket. A patch is a single large
 * blob — megabytes on a big branch — and the socket is shared with every live update in the app,
 * so sending it there would park it in front of the streaming tokens of every open thread. The
 * server serves it at `GET /api/pull-requests/:projectId/:number/diff` instead, scoped by the
 * same `p.pull_requests.view` as everything else and with the repository resolved from the
 * project.
 */
export interface PullRequestDiffResult {
  readonly patch: string;
  /** The commit the patch belongs to, so the reader can tell a stale view from a current one. */
  readonly headSha: string;
}

/**
 * A pull request operation the outside world refused, carrying the server's own reason.
 *
 * Thrown rather than returned so react-query treats it as the failure it is, while keeping the
 * one field that decides what the reader is told next. Both transports feed it: the HTTP diff
 * route answers `{ error, reason }` in its body, and the four websocket methods answer
 * `{ kind: "failed", failure }` — because neither a rate limit nor a missing token is a broken
 * call, and the websocket flattens a real failure into a sentence on the way out.
 */
export class PullRequestOperationError extends Error {
  readonly reason: PullRequestErrorReason;

  constructor(input: { message: string; reason: PullRequestErrorReason }) {
    super(input.message);
    this.name = "PullRequestOperationError";
    this.reason = input.reason;
  }
}

const KNOWN_ERROR_REASONS: ReadonlySet<string> = new Set<PullRequestErrorReason>([
  "no-credential",
  "credential-invalid",
  "forbidden",
  "not-github",
  "rate-limited",
  "upstream",
]);

const readReason = (value: unknown): PullRequestErrorReason =>
  typeof value === "string" && KNOWN_ERROR_REASONS.has(value)
    ? (value as PullRequestErrorReason)
    : "upstream";

interface FailedAnswer {
  readonly kind: "failed";
  readonly failure: { readonly reason: string; readonly message: string };
}

/**
 * Unwraps `{ kind: "ok", … } | { kind: "failed", failure }`, throwing the failure so every caller
 * sees one shape of error whichever transport it came from.
 *
 * The casts are the price of being generic over four different `ok` payloads: narrowing a type
 * parameter by its discriminant does not expose the other member's fields, and writing this four
 * times to avoid two casts would be worse.
 */
function unwrapAnswer<T extends { readonly kind: "ok" | "failed" }>(
  answer: T,
): Extract<T, { readonly kind: "ok" }> {
  if (answer.kind === "failed") {
    const { failure } = answer as unknown as FailedAnswer;
    throw new PullRequestOperationError({
      message: failure.message,
      reason: readReason(failure.reason),
    });
  }
  return answer as Extract<T, { readonly kind: "ok" }>;
}

async function fetchPullRequestDiff(ref: PullRequestRef): Promise<PullRequestDiffResult> {
  // Relative on purpose, as `serverOrigin.ts` says in so many words: an absolute origin is for
  // subresources the browser resolves against the page — an `<img src>` — while a `fetch` belongs
  // on the dev proxy. Naming the server directly sends this one cross-origin to :3773 in Vite dev,
  // where it dies as an opaque "Failed to fetch" with the diff tab showing nothing.
  const response = await fetch(
    `/api/pull-requests/${encodeURIComponent(ref.projectId)}/${ref.number}/diff`,
    { credentials: "include" },
  );

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    let reason: PullRequestErrorReason = "upstream";
    try {
      const body = (await response.json()) as { error?: string; reason?: string };
      if (body.error) message = body.error;
      if (body.reason) reason = readReason(body.reason);
    } catch {
      /* A body that is not the route's own JSON leaves the status as the whole story. */
    }
    throw new PullRequestOperationError({ message, reason });
  }

  return (await response.json()) as PullRequestDiffResult;
}

export function pullRequestsDiffQueryOptions(ref: PullRequestRef | null) {
  return queryOptions({
    queryKey: ref ? pullRequestQueryKeys.diff(ref) : (["pull-requests", "diff", null] as const),
    queryFn: async () => {
      if (!ref) throw new Error("Pull request diff is unavailable.");
      return fetchPullRequestDiff(ref);
    },
    enabled: ref !== null,
    staleTime: PULL_REQUEST_DIFF_STALE_TIME_MS,
    // No refetch on focus: coming back to the tab must not re-download megabytes of patch for a
    // commit that has not moved. The refresh button and every write action invalidate it.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

/**
 * Merge/close/reopen/ready. Invalidates that pull request's own detail and activity plus every
 * list page — the action can move the row out of whichever state filter is currently showing it.
 */
export function pullRequestsRunActionMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationKey: pullRequestMutationKeys.runAction,
    mutationFn: async (request: PullRequestRunActionInput) => {
      const api = ensureNativeApi();
      return unwrapAnswer(await api.pullRequests.runAction(request)).outcome;
    },
    onSuccess: async (_result, request) => {
      await Promise.all([
        invalidatePullRequestDetailQueries(input.queryClient, {
          projectId: request.projectId,
          number: request.number,
        }),
        invalidatePullRequestListQueries(input.queryClient),
      ]);
    },
  });
}

/**
 * Posts a conversation remark. Only that pull request's detail (the comment count) and activity
 * (the remark itself) change — the list is untouched, so it is left alone.
 */
export function pullRequestsCommentMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationKey: pullRequestMutationKeys.comment,
    mutationFn: async (request: PullRequestCommentInput) => {
      const api = ensureNativeApi();
      return unwrapAnswer(await api.pullRequests.comment(request)).comment;
    },
    onSuccess: async (_result, request) => {
      await invalidatePullRequestDetailQueries(input.queryClient, {
        projectId: request.projectId,
        number: request.number,
      });
    },
  });
}

/**
 * Forgets the server's own cache — one pull request's, or every listing's — then forgets the
 * matching client cache so the next read actually asks GitHub again.
 */
export function pullRequestsInvalidateMutationOptions(input: { queryClient: QueryClient }) {
  return mutationOptions({
    mutationKey: pullRequestMutationKeys.invalidate,
    mutationFn: async (request: PullRequestInvalidateInput) => {
      const api = ensureNativeApi();
      return api.pullRequests.invalidate(request);
    },
    onSuccess: async (_result, request) => {
      if (request.reference) {
        await invalidatePullRequestDetailQueries(input.queryClient, request.reference);
      } else {
        await invalidatePullRequestListQueries(input.queryClient);
      }
    },
  });
}
